// PharmacySelection component
// Full-page pharmacy selection with search and filtering
// Displays paginated list of pharmacies with search by NPI and zip code

import { Button, TextField, Chip } from '@smithhealth/design-system';
import { useState, type ReactElement } from 'react';
import { Layout } from '@components/layout/layout';
import { Divider } from '@components/sds/divider';
import {
  Table,
  type ColumnDefinition,
  type SortDescriptor,
} from '@components/sds/table';
import type {
  Pharmacy,
  PharmacySearchFilters,
  PharmacySortDescriptor,
} from '@services/pharmacy';
import { OrderDirectionEnum } from 'generated-schema-types';
import styles from './pharmacy-selection.module.scss';

export interface PharmacySelectionProperties {
  pharmacies: Pharmacy[];
  onSelectPharmacy: (pharmacy: Pharmacy) => void;
  searchFilters: PharmacySearchFilters;
  onSearchFiltersChange: (filters: PharmacySearchFilters) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: VoidFunction;
  onPreviousPage: VoidFunction;
  isLoading?: boolean;
  sortDescriptor: PharmacySortDescriptor;
  onSortChange: (sort: PharmacySortDescriptor) => void;
}

export const PharmacySelection = ({
  pharmacies,
  onSelectPharmacy,
  searchFilters,
  onSearchFiltersChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  isLoading = false,
  sortDescriptor,
  onSortChange,
}: PharmacySelectionProperties): ReactElement => {
  const [npiSearch, setNpiSearch] = useState(searchFilters.npi ?? '');
  const [zipSearch, setZipSearch] = useState(searchFilters.zip ?? '');

  const onSearchClick = () => {
    onSearchFiltersChange({
      npi: npiSearch || undefined,
      zip: zipSearch || undefined,
    });
  };

  const clearZipChip = () => {
    onSearchFiltersChange({
      npi: npiSearch || undefined,
    });

    setZipSearch('');
  };

  const clearNPIChip = () => {
    onSearchFiltersChange({
      zip: zipSearch || undefined,
    });

    setNpiSearch('');
  };

  const handleSort = (sort: SortDescriptor) => {
    // Convert table sort descriptor to pharmacy sort descriptor
    const field = sort.column as 'npi' | 'name' | 'nabp';

    // If clicking on the already sorted column, toggle direction
    let direction: OrderDirectionEnum;

    if (field === sortDescriptor.field) {
      direction =
        sortDescriptor.direction === OrderDirectionEnum.Asc
          ? OrderDirectionEnum.Desc
          : OrderDirectionEnum.Asc;
    } else {
      // New column, default to ASC
      direction = OrderDirectionEnum.Asc;
    }

    onSortChange({ field, direction });
  };

  // Convert pharmacy sort descriptor to table sort descriptor
  const tableSortDescriptor: SortDescriptor = {
    column: sortDescriptor.field,
    direction: sortDescriptor.direction as 'ASC' | 'DESC',
  };

  const columns: ColumnDefinition<Pharmacy>[] = [
    {
      field: 'npi',
      header: 'Pharmacy NPI',
      sortable: true,
    },
    {
      field: 'name',
      header: 'Pharmacy Name',
      sortable: true,
    },
    {
      field: 'nabp',
      header: 'NABP',
      align: 'right',
      sortable: true,
    },
    {
      field: 'location.address1',
      header: 'Address',
      valueFormatter: value => (value ? String(value) : '-'),
    },
    {
      field: 'location.city',
      header: 'City',
      valueFormatter: value => (value ? String(value) : '-'),
    },
    {
      field: 'location.state',
      header: 'State',
      valueFormatter: value => (value ? String(value) : '-'),
    },
    {
      header: '',
      sticky: 'right',
      render: (_value, pharmacy) => (
        <Button
          hierarchy="secondary"
          variant="brand"
          size="xsmall"
          icon="chevron-right"
          iconPosition="end"
          onClick={() => onSelectPharmacy(pharmacy)}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Layout wide showNavigation={false}>
      <div className={styles.root}>
        <div className={styles.header}>Select Location</div>
        <div className={styles.search}>
          <div className={styles.filters}>
            <div className={styles.filterHeader}>Search Filters</div>
            <Button
              hierarchy="primary"
              variant="brand"
              size="small"
              onClick={onSearchClick}
            >
              Search
            </Button>
            <TextField
              label="Pharmacy NPI"
              value={npiSearch}
              onChange={setNpiSearch}
              placeholder="Pharmacy NPI"
            />

            <TextField
              label="Zip Code"
              value={zipSearch}
              onChange={setZipSearch}
              placeholder="Zip Code"
            />
          </div>
          <Divider direction="vertical" />
          <div className={styles.results}>
            {(searchFilters.zip || searchFilters.npi) && (
              <div className={styles.activeFiltersHeader}>
                {npiSearch && (
                  <Chip
                    label={`Pharmacy NPI: ${searchFilters.npi}`}
                    variant="brand"
                    onDelete={() => clearNPIChip()}
                  />
                )}

                {zipSearch && (
                  <Chip
                    label={`Zip Code: ${searchFilters.zip}`}
                    variant="brand"
                    onDelete={() => clearZipChip()}
                  />
                )}
              </div>
            )}
            <Table
              data={pharmacies}
              columns={columns}
              getRowId={pharmacy => pharmacy.id}
              isLoading={isLoading}
              aria-label="Pharmacy locations"
              onSort={handleSort}
              sortDescriptor={tableSortDescriptor}
              stickyHeader
            />
            <div className={styles.pagination}>
              <Button
                hierarchy="secondary"
                variant="brand"
                size="small"
                onClick={onPreviousPage}
                disabled={!hasPreviousPage}
              >
                Previous
              </Button>
              <Button
                hierarchy="secondary"
                variant="brand"
                size="small"
                onClick={onNextPage}
                disabled={!hasNextPage}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
